export const CHANNELS = ["fcm", "apns", "webpush", "email", "sms", "whatsapp"] as const;
export type Channel = (typeof CHANNELS)[number];

/** Push channels: the credential is bound to the token, so there is no cross-credential failover (D-04). */
export const PUSH_CHANNELS = ["fcm", "apns", "webpush"] as const satisfies readonly Channel[];
export type PushChannel = (typeof PUSH_CHANNELS)[number];
export function isPushChannel(channel: Channel): channel is PushChannel {
  return (PUSH_CHANNELS as readonly Channel[]).includes(channel);
}

export const PROVIDER_STATUSES = ["active", "degraded", "disabled"] as const;
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];

export const CREDENTIAL_STATUSES = ["active", "disabled"] as const;
export type CredentialStatus = (typeof CREDENTIAL_STATUSES)[number];

export const APP_STATUSES = ["active", "suspended"] as const;
export type AppStatus = (typeof APP_STATUSES)[number];

export const MESSAGE_STRATEGIES = ["parallel", "fallback", "first_available"] as const;
export type MessageStrategy = (typeof MESSAGE_STRATEGIES)[number];

export const MESSAGE_PRIORITIES = ["high", "normal", "low"] as const;
export type MessagePriority = (typeof MESSAGE_PRIORITIES)[number];

export const MESSAGE_STATUSES = [
  "scheduled",
  "queued",
  "processing",
  "completed",
  "partially_failed",
  "failed",
  "cancelled",
] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const DELIVERY_STATUSES = [
  "pending",
  "sending",
  "sent",
  "delivered",
  "failed",
  "skipped",
  "bounced",
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export const DELIVERY_EVENT_TYPES = [
  "sent",
  "delivered",
  "failed",
  "retry_scheduled",
  "retry_requested",
  "bounced",
  "opened",
  "clicked",
  "unsubscribed",
] as const;
export type DeliveryEventType = (typeof DELIVERY_EVENT_TYPES)[number];

export const SUBSCRIPTION_STATUSES = ["active", "unsubscribed", "invalid"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/** Where a customer's location came from. Priority gps > backend > ip (docs/05). */
export const LOCATION_SOURCES = ["backend", "ip", "gps"] as const;
export type LocationSource = (typeof LOCATION_SOURCES)[number];

/**
 * Error codes of deliveries that were never sent because the recipient had nowhere to send to (D-37).
 * These rows have status `skipped` and an empty address when there was no subscription at all.
 */
export const SKIP_REASONS = [
  "customer_not_found",
  "no_subscription",
  "subscription_invalid",
  "unsubscribed",
] as const;
export type SkipReason = (typeof SKIP_REASONS)[number];

export const USER_ROLES = ["owner", "admin", "viewer"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const API_KEY_MODES = ["live", "test"] as const;
export type ApiKeyMode = (typeof API_KEY_MODES)[number];

export const API_KEY_SCOPES = [
  "messages:write",
  "messages:read",
  "customers:write",
  "customers:read",
] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

export const DEFAULT_CATEGORY = "transactional";

export const LIMITS = {
  maxRecipientsPerRequest: 1000,
  maxChannelsPerRequest: CHANNELS.length,
  fcmMaxPayloadBytes: 4096,
  maxDeliveryAttempts: 5,
  pageSizeDefault: 50,
  pageSizeMax: 200,
  /** Minimum length for a new dashboard user password. Sign-in itself only requires a non-empty value. */
  passwordMinLength: 8,
  maxSubscriptionsPerCustomer: 50,
  /** Longest period the overview statistics cover. */
  statsMaxDays: 90,
  /** Deliveries named one by one in a manual retry. Larger sets use a filter. */
  maxRetryIds: 1000,
  /** Deliveries one manual retry call handles; the dashboard calls again with the cursor for the rest. */
  retryBatchMax: 2000,
} as const;

/** Error codes returned in `data.code`. The dashboard uses them to translate messages. */
export const ERROR_CODES = [
  "validation_error",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "rate_limited",
  "internal_error",
  "invalid_api_key",
  "insufficient_scope",
  "app_suspended",
  "credential_not_allowed",
  "credential_channel_mismatch",
  "no_credential",
  "provider_unavailable",
  "no_recipients",
  "payload_too_large",
  "idempotency_conflict",
  "not_supported_yet",
  "invalid_credentials",
  "credential_in_use",
  "app_has_messages",
  "alias_taken",
  "slug_taken",
  "wrong_password",
  "same_password",
  "profile_in_use",
  "profile_name_taken",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];
